// Tour guiado interactivo del Búho-JASQ® (driver.js) — versión "súper completa".
//
// Dos modos:
//  1) startAppTour()  → recorrido rápido del menú lateral (auto-inicio 1ª vez).
//  2) runTutorial()   → tutoriales paso a paso por submódulo y rol, con
//     NAVEGACIÓN AUTOMÁTICA entre pantallas y, opcionalmente, VOZ del búho
//     (para estudiantes / niños pequeños).
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";
import { speakOwl, stopOwl } from "./owlVoice";

const visible = (el) => el && el.offsetParent !== null;
const currentPath = () => window.location.pathname;
const strip = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

function waitForEl(selector, timeout = 5000) {
  return new Promise((resolve) => {
    if (!selector) return resolve(null);
    const existing = document.querySelector(selector);
    if (existing) return resolve(existing);
    let done = false;
    const obs = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el && !done) { done = true; obs.disconnect(); resolve(el); }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => {
      if (!done) { obs.disconnect(); resolve(document.querySelector(selector)); }
    }, timeout);
  });
}

// ============================================================
//  Recorrido rápido del menú lateral (auto-inicio la 1ª vez)
// ============================================================
const COPY = {
  "nav-admin-usuarios": "Crea, edita y administra todos los usuarios y sus roles.",
  "nav-admin-jerarquia": "Define la estructura: Franquicia → Institución → Colegio → Salón.",
  "nav-admin-franquicias": "El punto de partida: crea y administra tus franquicias.",
  "nav-admin-cuestionarios": "Diseña y edita los cuestionarios CEP y CAD con fórmulas matemáticas.",
  "nav-admin-clases-vivo": "Programa e inicia clases en vivo con pizarra y grabación.",
  "nav-est-cuestionarios": "Aquí ves y resuelves tus diagnósticos asignados.",
  "nav-est-clases-vivo": "Únete a tus clases en vivo con tu profe.",
  "nav-doc-mis-estudiantes": "Consulta y acompaña a tus estudiantes asignados.",
  "nav-doc-clases-vivo": "Crea e inicia tus clases en vivo.",
};

export function startAppTour({ userName } = {}) {
  const steps = [];
  steps.push({
    popover: {
      title: `¡Hola${userName ? ", " + userName : ""}! 🦉`,
      description:
        "Soy <b>Zarek</b> 🦉. Te muestro la plataforma en menos de un minuto. " +
        "Pulsa el botón 🦉 arriba cuando quieras un <b>tutorial paso a paso</b> de cada módulo.",
    },
  });
  const kanban = document.querySelector('[data-testid="hub-kanban"]');
  if (visible(kanban)) {
    steps.push({
      element: '[data-testid="hub-kanban"]',
      popover: { title: "Tus aplicaciones", description: "Cada columna agrupa las aplicaciones por tema. Toca un icono para abrirla; con 🏠 vuelves aquí y con ← regresas." },
    });
  }
  const navEls = [...document.querySelectorAll('[data-testid^="hub-col-"]')].filter(visible);
  const seen = new Set();
  navEls.forEach((el) => {
    const tid = el.getAttribute("data-testid");
    if (!tid || seen.has(tid)) return;
    seen.add(tid);
    const label = (el.querySelector("h2")?.textContent || "").trim() || "Sección";
    steps.push({ element: `[data-testid="${tid}"]`, popover: { title: label, description: COPY[tid] || `Accede a <b>${label}</b>.` } });
  });
  const notif = document.querySelector('[data-testid="header-notifications"]');
  if (visible(notif)) steps.push({ element: '[data-testid="header-notifications"]', popover: { title: "Notificaciones", description: "Avisos de clases, diagnósticos y novedades aparecen aquí." } });
  const avatar = document.querySelector('[data-testid="header-user-avatar"]');
  if (visible(avatar)) steps.push({ element: '[data-testid="header-user-avatar"]', popover: { title: "Tu perfil", description: "Cambia tu avatar, edita tu información o cierra sesión." } });
  steps.push({
    popover: {
      title: "¡Listo! 🎉",
      description: "Pulsa el botón <b>🦉</b> arriba para abrir el menú de <b>tutoriales paso a paso</b> cuando quieras.",
    },
  });
  const d = driver({
    showProgress: true, allowClose: true, overlayColor: "#1F2547", overlayOpacity: 0.62,
    stagePadding: 6, stageRadius: 12, popoverClass: "jasq-tour",
    nextBtnText: "Siguiente →", prevBtnText: "← Atrás", doneBtnText: "¡Listo!",
    progressText: "{{current}} de {{total}}", steps,
  });
  d.drive();
  return d;
}

// ============================================================
//  TUTORIALES PASO A PASO (por rol y submódulo)
// ============================================================
// Cada paso: { route?, el?, title, desc, side? }
//  - route: navega automáticamente a esa pantalla antes de mostrar el paso.
//  - el:    selector a resaltar (si no se encuentra, se muestra centrado).

const T = {
  admin: [
    {
      id: "overview", emoji: "🧭", title: "Recorrido general",
      summary: "Conoce el menú y las secciones principales en 1 minuto.",
      overview: true,
    },
    {
      id: "franquicia", emoji: "🏢", title: "Crear una franquicia",
      summary: "El primer paso: dar de alta una franquicia (la raíz de todo).",
      steps: [
        { route: "/admin/franquicias", el: '[data-testid="admin-franquicias-page"]', title: "Franquicias", desc: "Esta es la sección <b>Franquicias</b>, la raíz de toda tu estructura. Aquí ves todas las franquicias creadas." },
        { route: "/admin/franquicias", el: '[data-testid="open-create-franchise"]', title: "1) Nueva franquicia", desc: "Pulsa <b>Nueva franquicia</b>. Se abre un asistente paso a paso.", side: "bottom" },
        { route: "/admin/franquicias", el: '[data-testid="open-create-franchise"]', title: "2) Datos básicos", desc: "En el asistente escribes el <b>nombre</b>, la <b>ciudad</b> y el <b>país</b> de la franquicia." },
        { route: "/admin/franquicias", el: '[data-testid="open-create-franchise"]', title: "3) Representante", desc: "Luego defines el <b>dueño/representante</b> (nombre, email y clave). Se creará como administrador de esa franquicia (rol <i>franquiciado</i>)." },
        { route: "/admin/franquicias", el: '[data-testid="open-create-franchise"]', title: "4) Finalizar", desc: "Pulsa <b>Crear</b> y la franquicia aparecerá en la lista. ¡Listo para sumar instituciones!" },
      ],
    },
    {
      id: "jerarquia", emoji: "🏫", title: "Crear Institución, Colegio y Salón",
      summary: "Arma la estructura completa de tu franquicia.",
      steps: [
        { route: "/admin/jerarquia", el: '[data-testid="admin-jerarquia"]', title: "Jerarquía", desc: "La estructura es: <b>Franquicia → Institución → Colegio → Salón</b>." },
        { route: "/admin/jerarquia", el: '[data-testid="jerarquia-tabs"]', title: "Pestañas", desc: "Cambia entre <b>Instituciones</b>, <b>Colegios</b> y <b>Salones</b> con estas pestañas." },
        { route: "/admin/jerarquia", el: '[data-testid="jerarquia-tabs"]', title: "1) Institución", desc: "En <b>Instituciones</b> creas una nueva con su <b>DNI</b> (único por franquicia), <b>dirección</b>, <b>contacto</b>, <b>actividad económica</b> y su <b>Representante Legal</b> (que se crea como administrador de la franquicia)." },
        { route: "/admin/jerarquia", el: '[data-testid="jerarquia-tabs"]', title: "2) Colegio", desc: "En <b>Colegios</b> creas un colegio dentro de una institución, con sus mismos datos (DNI, dirección, contacto…)." },
        { route: "/admin/jerarquia", el: '[data-testid="classroom-new-btn"]', title: "3) Salón", desc: "En <b>Salones</b> pulsa este botón para crear un salón: eliges franquicia, institución, colegio, <b>grado</b> y <b>capacidad</b>.", side: "bottom" },
        { route: "/admin/jerarquia", el: '[data-testid="admin-jerarquia"]', title: "¡Estructura lista!", desc: "Con esto ya puedes asignar usuarios (docentes y estudiantes) a cada salón." },
      ],
    },
    {
      id: "usuarios", emoji: "👥", title: "Crear usuarios y asignarlos",
      summary: "Da de alta usuarios y asígnalos masivamente a la jerarquía.",
      steps: [
        { route: "/admin/usuarios", el: '[data-testid="admin-users-page"]', title: "Usuarios", desc: "Aquí administras a todas las personas: admins, franquiciados, docentes y estudiantes." },
        { route: "/admin/usuarios", el: '[data-testid="open-create-user"]', title: "1) Nuevo usuario", desc: "Pulsa <b>Nuevo usuario</b> para crear una persona: nombre, email, rol, edad y franquicia.", side: "bottom" },
        { route: "/admin/usuarios", el: '[data-testid="bulk-assign-hierarchy-btn"]', title: "2) Asignación masiva", desc: "Selecciona varios usuarios y pulsa aquí para asignarlos en bloque a una <b>institución, colegio y salón</b>.", side: "bottom" },
        { route: "/admin/usuarios", el: '[data-testid="bulk-assign-teacher-btn"]', title: "3) Asignar docente", desc: "También puedes asignar un <b>docente</b> a varios estudiantes a la vez.", side: "bottom" },
      ],
    },
    {
      id: "roles", emoji: "🛡️", title: "Crear roles y perfiles de acceso",
      summary: "Define qué puede ver y hacer cada tipo de usuario.",
      steps: [
        { route: "/admin/perfiles-permisos", el: '[data-testid="admin-perfiles-permisos-page"]', title: "Perfiles de acceso", desc: "Un <b>perfil</b> agrupa permisos (qué módulos puede ver una persona)." },
        { route: "/admin/perfiles-permisos", el: '[data-testid="open-create-profile"]', title: "1) Nuevo perfil", desc: "Pulsa aquí, ponle nombre y marca los módulos permitidos.", side: "bottom" },
        { route: "/admin/roles", el: '[data-testid="admin-roles-page"]', title: "Roles", desc: "Un <b>rol</b> es un cargo (ej. coordinador) que se vincula a un perfil de acceso." },
        { route: "/admin/roles", el: '[data-testid="open-create-role"]', title: "2) Nuevo rol", desc: "Crea un rol con su nombre, descripción y el <b>perfil</b> que tendrá.", side: "bottom" },
      ],
    },
    {
      id: "cuestionarios", emoji: "📝", title: "Crear cuestionarios CEP y CAD",
      summary: "Diseña diagnósticos emocionales (CEP) y académicos (CAD).",
      steps: [
        { route: "/admin/cuestionarios", el: '[data-testid="admin-cuestionarios-page"]', title: "Cuestionarios", desc: "Aquí diseñas las plantillas <b>CEP</b> (emocional) y <b>CAD</b> (académico)." },
        { route: "/admin/cuestionarios", el: '[data-testid="tpl-kind-tabs"]', title: "1) Elige el tipo", desc: "Cambia entre <b>CEP</b> y <b>CAD</b> con estas pestañas.", side: "bottom" },
        { route: "/admin/cuestionarios", el: '[data-testid="tpl-create-btn"]', title: "2) Nueva plantilla", desc: "Pulsa para crear una plantilla: código, título y mensaje de bienvenida de Zarek.", side: "bottom" },
        { route: "/admin/cuestionarios", el: '[data-testid="add-block"]', title: "3) Agregar bloques", desc: "Añade <b>bloques</b> y dentro de cada bloque tus <b>preguntas</b> (opción múltiple, escalas, matemáticas con fórmulas KaTeX, etc.).", side: "bottom" },
        { route: "/admin/cuestionarios", el: '[data-testid="tpl-save"]', title: "4) Guardar", desc: "Guarda tu plantilla. Para los CAD puedes abrir el editor avanzado con banco de preguntas.", side: "bottom" },
      ],
    },
    {
      id: "asignar", emoji: "📤", title: "Asignar diagnósticos",
      summary: "Envía un cuestionario a un estudiante con fecha.",
      steps: [
        { route: "/admin/resultados-cep", el: '[data-testid="docente-cuestionarios"]', title: "Asignaciones", desc: "Aquí ves y creas las asignaciones de cuestionarios a estudiantes." },
        { route: "/admin/resultados-cep", el: '[data-testid="open-create-assignment"]', title: "1) Nueva asignación", desc: "Pulsa <b>Asignar</b> para abrir el formulario.", side: "bottom" },
        { route: "/admin/resultados-cep", el: '[data-testid="open-create-assignment"]', title: "2) Completa", desc: "Elige el <b>estudiante</b>, la <b>plantilla</b> (CEP/CAD) y la <b>fecha</b>. Guarda y el estudiante la verá en su panel." },
      ],
    },
    {
      id: "clases", emoji: "🎥", title: "Programar una clase en vivo",
      summary: "Agenda una videoclase con pizarra y grabación.",
      steps: [
        { route: "/admin/clases-en-vivo", el: '[data-testid="live-classes-page"]', title: "Clases en vivo", desc: "Aquí programas, inicias y revisas tus clases en vivo." },
        { route: "/admin/clases-en-vivo", el: '[data-testid="schedule-class-btn"]', title: "1) Programar clase", desc: "Pulsa para abrir el formulario de agenda.", side: "bottom" },
        { route: "/admin/clases-en-vivo", el: '[data-testid="schedule-class-btn"]', title: "2) Detalles", desc: "Define <b>fecha/hora</b>, <b>duración</b> y descripción. Al iniciarla tendrás <b>pizarra</b>, <b>video</b> y <b>grabación</b>." },
      ],
    },
  ],

  franquiciado: [
    { id: "overview", emoji: "🧭", title: "Recorrido general", summary: "Conoce tu panel de franquicia en 1 minuto.", overview: true },
    {
      id: "mi-franquicia", emoji: "🏢", title: "Mi franquicia",
      summary: "Revisa los datos y resumen de tu franquicia.",
      steps: [
        { route: "/franquiciado/franquicia", el: '[data-testid="franquiciado-franquicia"]', title: "Mi franquicia", desc: "Aquí ves el logo, los datos y el resumen (instituciones, colegios, usuarios) de tu franquicia." },
      ],
    },
    {
      id: "jerarquia", emoji: "🏫", title: "Crear Institución, Colegio y Salón",
      summary: "Arma la estructura de tu franquicia.",
      steps: [
        { route: "/franquiciado/jerarquia", el: '[data-testid="admin-jerarquia"]', title: "Jerarquía", desc: "Estructura: <b>Institución → Colegio → Salón</b> (dentro de tu franquicia)." },
        { route: "/franquiciado/jerarquia", el: '[data-testid="jerarquia-tabs"]', title: "Pestañas", desc: "Cambia entre Instituciones, Colegios y Salones." },
        { route: "/franquiciado/jerarquia", el: '[data-testid="jerarquia-tabs"]', title: "1) Institución / Colegio", desc: "Crea con <b>DNI</b> (único), dirección, contacto, actividad económica y <b>Representante Legal</b>." },
        { route: "/franquiciado/jerarquia", el: '[data-testid="classroom-new-btn"]', title: "2) Salón", desc: "Crea un salón con su grado y capacidad.", side: "bottom" },
      ],
    },
    {
      id: "usuarios", emoji: "👥", title: "Crear y asignar usuarios",
      summary: "Da de alta docentes y estudiantes de tu franquicia.",
      steps: [
        { route: "/franquiciado/usuarios", el: '[data-testid="admin-users-page"]', title: "Mis usuarios", desc: "Administras a los usuarios de tu franquicia." },
        { route: "/franquiciado/usuarios", el: '[data-testid="open-create-user"]', title: "1) Nuevo usuario", desc: "Crea docentes y estudiantes con su rol, edad y datos.", side: "bottom" },
        { route: "/franquiciado/usuarios", el: '[data-testid="bulk-assign-hierarchy-btn"]', title: "2) Asignación masiva", desc: "Asigna varios usuarios a institución, colegio y salón de una sola vez.", side: "bottom" },
      ],
    },
    {
      id: "asignar", emoji: "📤", title: "Asignar diagnósticos",
      summary: "Envía cuestionarios a tus estudiantes.",
      steps: [
        { route: "/franquiciado/asignar-cuestionarios", el: '[data-testid="docente-cuestionarios"]', title: "Asignar diagnósticos", desc: "Aquí asignas cuestionarios CEP/CAD a tus estudiantes." },
        { route: "/franquiciado/asignar-cuestionarios", el: '[data-testid="open-create-assignment"]', title: "Nueva asignación", desc: "Elige estudiante, plantilla y fecha. Guarda y aparecerá en su panel.", side: "bottom" },
      ],
    },
    {
      id: "clases", emoji: "🎥", title: "Programar clase en vivo",
      summary: "Agenda videoclases para tu franquicia.",
      steps: [
        { route: "/franquiciado/clases-en-vivo", el: '[data-testid="live-classes-page"]', title: "Clases en vivo", desc: "Programa e inicia clases en vivo." },
        { route: "/franquiciado/clases-en-vivo", el: '[data-testid="schedule-class-btn"]', title: "Programar", desc: "Define fecha, duración y descripción. Tendrás pizarra, video y grabación.", side: "bottom" },
      ],
    },
  ],

  docente: [
    { id: "overview", emoji: "🧭", title: "Recorrido general", summary: "Conoce tu panel docente en 1 minuto.", overview: true },
    {
      id: "estudiantes", emoji: "🧑‍🎓", title: "Mis estudiantes",
      summary: "Consulta y acompaña a tus estudiantes asignados.",
      steps: [
        { route: "/docente/mis-estudiantes", el: '[data-testid="docente-mis-estudiantes-page"]', title: "Mis estudiantes", desc: "Aquí ves a tus estudiantes asignados y su progreso. Haz clic en uno para ver su detalle." },
      ],
    },
    {
      id: "asignar", emoji: "📤", title: "Asignar un diagnóstico",
      summary: "Envía un cuestionario CEP/CAD a un estudiante.",
      steps: [
        { route: "/docente/cuestionarios", el: '[data-testid="docente-cuestionarios"]', title: "Asignar diagnósticos", desc: "Aquí creas y administras las asignaciones de tus estudiantes." },
        { route: "/docente/cuestionarios", el: '[data-testid="open-create-assignment"]', title: "1) Nueva asignación", desc: "Pulsa <b>Asignar</b> para abrir el formulario.", side: "bottom" },
        { route: "/docente/cuestionarios", el: '[data-testid="open-create-assignment"]', title: "2) Completa", desc: "Elige <b>estudiante</b>, <b>plantilla</b> y <b>fecha</b>. Al guardar, el estudiante la verá en sus diagnósticos." },
      ],
    },
    {
      id: "clases", emoji: "🎥", title: "Iniciar una clase en vivo",
      summary: "Agenda e inicia tus videoclases.",
      steps: [
        { route: "/docente/clases-en-vivo", el: '[data-testid="live-classes-page"]', title: "Clases en vivo", desc: "Programa, inicia y revisa tus clases en vivo con tus estudiantes." },
        { route: "/docente/clases-en-vivo", el: '[data-testid="schedule-class-btn"]', title: "Programar", desc: "Define fecha, duración y descripción. Al iniciar tendrás pizarra, video y grabación.", side: "bottom" },
      ],
    },
    {
      id: "resultados", emoji: "📊", title: "Ver resultados",
      summary: "Revisa los diagnósticos académicos por área.",
      steps: [
        { route: "/docente/resultados-academicos", el: '[data-testid="docente-resultados-academicos"]', title: "Diagnósticos académicos", desc: "Aquí ves el desempeño de tus estudiantes por área de conocimiento." },
      ],
    },
    {
      id: "grabaciones", emoji: "🎞️", title: "Mis grabaciones",
      summary: "Encuentra las grabaciones de tus clases.",
      steps: [
        { route: "/docente/grabaciones", title: "Grabaciones", desc: "Aquí quedan guardadas las grabaciones de tus clases en vivo para volver a verlas o compartirlas." },
      ],
    },
  ],

  estudiante: [
    { id: "overview", emoji: "🦉", title: "Conoce tu espacio", summary: "Zarek te muestra todo en 1 minuto.", overview: true },
    {
      id: "perfil", emoji: "🧑‍🚀", title: "Mi perfil y mi avatar",
      summary: "Cambia tu foto/avatar y tus datos.",
      kidFriendly: true,
      steps: [
        { route: "/estudiante/perfil", el: '[data-testid="estudiante-perfil-page"]', title: "Tu perfil", desc: "¡Este es tu espacio! Aquí ves tu información y tu avatar." },
        { route: "/estudiante/perfil", el: '[data-testid="perfil-edit-btn"]', title: "Cambia tu avatar", desc: "Pulsa aquí para elegir un avatar genial o subir tu foto. ¡Hazlo tuyo!", side: "bottom" },
      ],
    },
    {
      id: "evaluaciones", emoji: "⭐", title: "Mis diagnósticos",
      summary: "Encuentra y empieza tus cuestionarios.",
      kidFriendly: true,
      steps: [
        { route: "/estudiante/cuestionarios", el: '[data-testid="est-cuestionarios"]', title: "Tus cuestionarios", desc: "Aquí están tus diagnósticos. Las <b>pendientes</b> arriba y las <b>terminadas</b> abajo." },
        { route: "/estudiante/cuestionarios", el: '[data-testid="est-cuestionarios"]', title: "¡A empezar!", desc: "Pulsa el botón de una tarjeta para comenzar. Zarek te va a acompañar y, si quieres, ¡te leerá las preguntas en voz alta! 🦉" },
      ],
    },
    {
      id: "clases", emoji: "🎥", title: "Unirme a una clase en vivo",
      summary: "Entra a tus videoclases con tu profe.",
      kidFriendly: true,
      steps: [
        { route: "/estudiante/clases-en-vivo", el: '[data-testid="live-classes-page"]', title: "Clases en vivo", desc: "Aquí están tus clases. Cuando una esté <b>en vivo</b>, pulsa <b>Unirme</b> para entrar con tu profe." },
      ],
    },
    {
      id: "ruta", emoji: "🌱", title: "Mi ruta evolutiva",
      summary: "Mira cómo vas creciendo.",
      kidFriendly: true,
      steps: [
        { route: "/estudiante/ruta", el: '[data-testid="estudiante-ruta-page"]', title: "Tu ruta", desc: "¡Mira todo lo que has avanzado! Aquí ves tu progreso y tus próximos pasos. 🚀" },
      ],
    },
  ],
};

export function getTutorials(user) {
  return T[user?.role] || [];
}

// Ejecuta un tutorial paso a paso con navegación automática y voz opcional.
export function runTutorial(tut, { navigate, voiceEnabled = false, kids = false } = {}) {
  if (!tut) return null;
  if (tut.overview) {
    return startAppTour({ userName: undefined });
  }
  const tsteps = tut.steps || [];
  if (!tsteps.length) return null;

  // Copia mutable de steps para driver (permite quitar element si no existe).
  const steps = tsteps.map((s) => ({
    element: s.el || undefined,
    popover: { title: `${tut.emoji} ${s.title}`, description: s.desc, ...(s.side ? { side: s.side } : {}) },
  }));

  let d;
  let activeIdx = 0; // Track index manually (survives d.destroy())

  const prepare = async (index) => {
    const s = tsteps[index];
    if (!s) return;
    if (s.route && currentPath() !== s.route) {
      // CRITICAL: destroy overlay BEFORE route change to avoid
      // NotFoundError: removeChild on stale DOM nodes (Bug BLOQUE 0).
      try { d.destroy(); } catch (_) {}
      navigate(s.route);
      await new Promise((r) => setTimeout(r, 350)); // Wait for React render
    }
    if (s.el) {
      const found = await waitForEl(s.el);
      steps[index].element = found ? s.el : undefined;
    } else {
      await new Promise((r) => setTimeout(r, 180));
    }
  };

  const sayStep = (index) => {
    if (!voiceEnabled) return;
    const s = tsteps[index];
    if (s) speakOwl(`${s.title}. ${strip(s.desc)}`);
  };

  d = driver({
    showProgress: true,
    allowClose: true,
    overlayColor: "#1F2547",
    overlayOpacity: 0.66,
    stagePadding: 8,
    stageRadius: 14,
    popoverClass: kids ? "jasq-tour jasq-tour-kids" : "jasq-tour",
    nextBtnText: "Siguiente →",
    prevBtnText: "← Atrás",
    doneBtnText: "¡Listo!",
    progressText: "Paso {{current}} de {{total}}",
    steps,
    onHighlighted: (el, step, opts) => {
      try { activeIdx = opts.state.activeIndex; sayStep(activeIdx); } catch (_) {}
    },
    onNextClick: async () => {
      stopOwl();
      if (activeIdx + 1 >= steps.length) { d.destroy(); return; }
      activeIdx++;
      await prepare(activeIdx);
      d.drive(activeIdx); // Always re-drive (safe after destroy + navigate)
    },
    onPrevClick: async () => {
      stopOwl();
      if (activeIdx - 1 < 0) return;
      activeIdx--;
      await prepare(activeIdx);
      d.drive(activeIdx);
    },
    onCloseClick: () => { stopOwl(); d.destroy(); },
    onDestroyed: () => { stopOwl(); },
  });

  (async () => {
    await prepare(0);
    d.drive(0);
  })();

  return d;
}

// Marca/consulta si el usuario ya vio el tour (para auto-iniciarlo una vez).
const tourKey = (userId) => `jasq_tour_done_${userId || "anon"}`;
export const hasSeenTour = (userId) => {
  try { return localStorage.getItem(tourKey(userId)) === "1"; } catch { return true; }
};
export const markTourSeen = (userId) => {
  try { localStorage.setItem(tourKey(userId), "1"); } catch (_) { /* noop */ }
};
